/** Count semantics shared by legend formatting, filter totals and feed loading. */
export type FeedResultCountKey = 'aircraft_count' | 'vessel_count' | 'vehicle_count' | 'transhipment_count';

export function getFeedResultCountKey(feedType?: string): FeedResultCountKey | null {
  switch (String(feedType ?? '').trim().toLowerCase()) {
    case 'aircraft': return 'aircraft_count';
    case 'ship':
    case 'vessel':
    case 'vessel-classification': return 'vessel_count';
    case 'vehicle-classification': return 'vehicle_count';
    case 'transshipment':
    case 'transhipment': return 'transhipment_count';
    default: return null;
  }
}

export function parseFeedResultCount(value: unknown): number | null {
  const count = typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')
    ? Number(value) : NaN;
  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

/** Collapse case variants of the feed's total into one canonical key without mutating metadata. */
export function normalizeFeedResultCount(feedResults: any, feedType?: string): any {
  const key = getFeedResultCountKey(feedType);
  if (!key || !feedResults || typeof feedResults !== 'object' || Array.isArray(feedResults)) return feedResults;
  const matchingKey = Object.keys(feedResults).find((field) => field.toLowerCase() === key);
  if (!matchingKey) return feedResults;

  // A canonical value supplied by a live update wins, including zero and loading/error text.
  const count = feedResults[key] ?? feedResults[matchingKey];
  return Object.fromEntries(Object.entries(feedResults).map(([field, value]) =>
    field.toLowerCase() === key ? [key, count] : [field, value]
  ));
}

export function getFeedResultCount(feedResults: any, feedType?: string): number | null {
  const key = getFeedResultCountKey(feedType);
  // Never infer a total from class buckets, area, percentages or formatted labels.
  if (!key) return null;
  if (feedResults && typeof feedResults === 'object') {
    return parseFeedResultCount(normalizeFeedResultCount(feedResults, feedType)[key]);
  }
  return parseFeedResultCount(feedResults);
}
